import { X, Tag, Palette } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const TagModal = ({ isOpen, onClose, onSave, tag = null }) => {
  const modalRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    backgroundColor: "#3b82f6",
    textColor: "#ffffff",
  });

  const [errors, setErrors] = useState({});

  const commonColors = [
    { bg: "#ef4444", text: "#ffffff" },
    { bg: "#f97316", text: "#ffffff" },
    { bg: "#f59e0b", text: "#ffffff" },
    { bg: "#eab308", text: "#000000" },
    { bg: "#84cc16", text: "#000000" },
    { bg: "#22c55e", text: "#ffffff" },
    { bg: "#10b981", text: "#ffffff" },
    { bg: "#14b8a6", text: "#ffffff" },
    { bg: "#06b6d4", text: "#ffffff" },
    { bg: "#0ea5e9", text: "#ffffff" },
    { bg: "#3b82f6", text: "#ffffff" },
    { bg: "#6366f1", text: "#ffffff" },
    { bg: "#8b5cf6", text: "#ffffff" },
    { bg: "#a855f7", text: "#ffffff" },
    { bg: "#d946ef", text: "#ffffff" },
    { bg: "#ec4899", text: "#ffffff" },
    { bg: "#f43f5e", text: "#ffffff" },
    { bg: "#64748b", text: "#ffffff" },
  ];

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleEsc);
    }
    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

  useEffect(() => {
    if (tag) {
      setFormData({
        name: tag.name || "",
        backgroundColor: tag.backgroundColor || "#3b82f6",
        textColor: tag.textColor || "#ffffff",
      });
    } else {
      setFormData({
        name: "",
        backgroundColor: "#3b82f6",
        textColor: "#ffffff",
      });
    }
    setErrors({});
  }, [tag, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Nome é obrigatório";
    if (!formData.backgroundColor.match(/^#[0-9A-F]{6}$/i))
      newErrors.backgroundColor = "Cor de fundo inválida";
    if (!formData.textColor.match(/^#[0-9A-F]{6}$/i))
      newErrors.textColor = "Cor de texto inválida";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave({
        ...formData,
        id: tag?.id,
        usageCount: tag?.usageCount || 0,
        active: tag?.active !== undefined ? tag.active : true,
        createdAt: tag?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const selectColorPreset = (color) => {
    setFormData((prev) => ({
      ...prev,
      backgroundColor: color.bg,
      textColor: color.text,
    }));
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOverlayClick}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
        >
          <motion.div
            ref={modalRef}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-2xl rounded-lg bg-white shadow-xl dark:bg-gray-900"
          >
            <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
              <h2 className="flex items-center text-xl font-semibold text-gray-900 dark:text-gray-100">
                <Tag className="mr-2 h-5 w-5" />
                {tag ? "Editar Tag" : "Nova Tag"}
              </h2>
              <button
                onClick={onClose}
                className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-5">
                <div>
                  <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Tag className="mr-2 h-4 w-4" />
                    Nome da Tag *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full rounded-lg border ${
                      errors.name
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-green-500 focus:ring-green-500"
                    } bg-white px-4 py-2 text-gray-900 transition-colors focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100`}
                    placeholder="Digite o nome da tag"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                      <Palette className="mr-2 h-4 w-4" />
                      Cor de Fundo *
                    </label>
                    <div className="flex items-center space-x-2">
                      <div
                        style={{ backgroundColor: formData.backgroundColor }}
                        className="h-6 w-6 cursor-pointer rounded-full border-2 border-gray-300 dark:border-gray-600"
                        onClick={() =>
                          document
                            .getElementById("backgroundColorPicker")
                            .click()
                        }
                      />
                      <input
                        id="backgroundColorPicker"
                        type="color"
                        name="backgroundColor"
                        value={formData.backgroundColor}
                        onChange={handleChange}
                        className="hidden"
                      />
                      <input
                        type="text"
                        name="backgroundColor"
                        value={formData.backgroundColor}
                        onChange={handleChange}
                        className={`flex-1 rounded-lg border ${
                          errors.backgroundColor
                            ? "border-red-500"
                            : "border-gray-300"
                        } bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100`}
                        placeholder="#3b82f6"
                      />
                    </div>
                    {errors.backgroundColor && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.backgroundColor}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                      <Palette className="mr-2 h-4 w-4" />
                      Cor do Texto *
                    </label>
                    <div className="flex items-center space-x-2">
                      <div
                        style={{ backgroundColor: formData.textColor }}
                        className="h-6 w-6 cursor-pointer rounded-full border-2 border-gray-300 dark:border-gray-600"
                        onClick={() =>
                          document.getElementById("textColorPicker").click()
                        }
                      />
                      <input
                        id="textColorPicker"
                        type="color"
                        name="textColor"
                        value={formData.textColor}
                        onChange={handleChange}
                        className="hidden"
                      />
                      <input
                        type="text"
                        name="textColor"
                        value={formData.textColor}
                        onChange={handleChange}
                        className={`flex-1 rounded-lg border ${
                          errors.textColor
                            ? "border-red-500"
                            : "border-gray-300"
                        } bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100`}
                        placeholder="#ffffff"
                      />
                    </div>
                    {errors.textColor && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.textColor}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Cores Predefinidas
                  </label>
                  <div className="grid grid-cols-9 gap-2">
                    {commonColors.map((color, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => selectColorPreset(color)}
                        className="group relative h-10 w-10 rounded-lg border-2 transition-all hover:scale-110"
                        style={{
                          backgroundColor: color.bg,
                          borderColor:
                            formData.backgroundColor === color.bg &&
                            formData.textColor === color.text
                              ? "#000"
                              : "transparent",
                        }}
                        title={`${color.bg} / ${color.text}`}
                      >
                        <span
                          className="absolute inset-0 flex items-center justify-center text-xs font-bold"
                          style={{ color: color.text }}
                        >
                          A
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="border-t border-gray-200 p-4 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <span
                      className="rounded-full px-4 py-2 text-sm font-medium"
                      style={{
                        backgroundColor: formData.backgroundColor,
                        color: formData.textColor,
                      }}
                    >
                      {formData.name || "Nome da Tag"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 border-t border-gray-200 pt-6 dark:border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-600"
                >
                  {tag ? "Salvar Alterações" : "Criar Tag"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TagModal;
